import { type RefObject, useEffect } from 'react';

import type { Post } from '~/interfaces/post';
import { getUrlParamValue } from '~/utils/getUrlParamValue';
import { isValidUrl } from '~/utils/isValidUrl';

interface Props {
  post: Pick<Post, 'id' | 'publication_id'>;
  visitToken: string;
  skip?: boolean;
  containerRef?: RefObject<HTMLElement | null>;
}

const useClickTracking = ({ post, visitToken, skip = false, containerRef }: Props) => {
  useEffect(() => {
    if (skip) return;
    function trackClick(e: Event) {
      try {
        let target = e.target as HTMLElement;
        while (
          target &&
          target.id !== 'content-blocks' && // bail if we reach the content-blocks
          target.tagName !== 'BODY' // bail if we reach the body
        ) {
          if (target.tagName === 'A') {
            const url = (target as HTMLAnchorElement).href;
            if (!isValidUrl(url)) {
              break;
            }
            const linkId = getUrlParamValue(url, '_bhlid');
            const payload = {
              url,
              post_id: post.id,
              publication_id: post.publication_id,
              visit_token: visitToken,
              ...(linkId ? { link_id: linkId } : {}),
            };
            fetch('/clicks', {
              method: 'post',
              body: JSON.stringify(payload),
              headers: {
                'content-type': 'application/json',
              },
            })
              .then(/* do nothing */)
              .catch(/* do nothing */);

            break;
          }
          target = target.parentElement as HTMLElement;
        }
      } catch {
        // do nothing
      }
    }

    const listenerTarget = containerRef ? containerRef.current : document;
    if (!listenerTarget) return;

    listenerTarget.addEventListener('click', trackClick);

    return () => {
      listenerTarget.removeEventListener('click', trackClick);
    };
  }, [post.id, post.publication_id, visitToken, skip, containerRef]);
};

export default useClickTracking;
